"use client";

import { useTranslations } from "next-intl";
import { ArrowLeft } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";

/**
 * A legal page rendered from a section list rather than hard-coded blocks.
 *
 * Each operator has its own section list and complete document catalogue.
 * Explicit lists prevent inactive clauses from appearing when the profile or
 * interface language changes.
 */
export interface LegalSection {
  /** Key stem: "collect" reads `<ns>.collectTitle` and `<ns>.collectText`. */
  key: string;
  /** Renders `<ns>.<key>Item1..N` as a bullet list under the text. */
  items?: number;
  /** Renders `<ns>.<key>Note` as a trailing paragraph. */
  note?: boolean;
  /** Omits the body paragraph — for sections that are only a list. */
  textless?: boolean;
  /** Optional authoritative references shown below the section copy. */
  links?: readonly {
    href: string;
    labelKey: string;
  }[];
}

export function LegalDocument({
  namespace,
  sections,
}: {
  namespace: "privacy" | "terms" | "refund" | "contacts";
  sections: readonly LegalSection[];
}) {
  const t = useTranslations("legal");
  const footer = useTranslations("landing.footer");
  const { brand, isGetmint } = useSiteFlavor();

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <Link
          href="/"
          className="mb-8 inline-flex items-center gap-2 rounded-sm text-base text-ink-mute transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mint"
        >
          <ArrowLeft size={20} />
          {t("back")}
        </Link>

        <h1 className="text-3xl font-bold tracking-tight text-ink">
          {t(`${namespace}.title`)}
        </h1>
        <p className="mt-2 text-sm text-ink-mute">
          {t(`${namespace}.lastUpdated`)}
        </p>

        <div className="mt-8 space-y-8">
          {sections.map(({ key, items, note, textless, links }) => (
            <section key={key}>
              <h2 className="text-lg font-semibold text-ink">
                {t(`${namespace}.${key}Title`)}
              </h2>
              <div className="mt-2 text-sm leading-relaxed text-ink-dim">
                {!textless && <p>{t(`${namespace}.${key}Text`)}</p>}
                {items ? (
                  <ul className="mt-3 list-disc space-y-1 pl-5">
                    {Array.from({ length: items }, (_, i) => (
                      <li key={i}>{t(`${namespace}.${key}Item${i + 1}`)}</li>
                    ))}
                  </ul>
                ) : null}
                {note && <p className="mt-3">{t(`${namespace}.${key}Note`)}</p>}
                {links?.length ? (
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                    {links.map(({ href, labelKey }) => (
                      <a
                        key={href}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-sm font-medium text-mint underline decoration-mint/40 underline-offset-4 transition-colors hover:text-mint-dark focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mint"
                      >
                        {t(`${namespace}.${labelKey}`)}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>
          ))}
        </div>
        {isGetmint && (
          <nav aria-label={t("back")} className="mt-12 flex flex-wrap gap-x-5 gap-y-3 border-t border-border pt-6 text-sm text-ink-dim [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-ink">
            <Link href="/pricing">{footer("pricing")}</Link>
            <Link href="/terms">{footer("terms")}</Link>
            <Link href="/privacy">{footer("privacy")}</Link>
            <Link href="/refund-policy">{footer("refundPolicy")}</Link>
            {brand.legalProfile === "grigorets" && <Link href="/contacts">{footer("contacts")}</Link>}
            {brand.supportEmail && <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>}
          </nav>
        )}
      </div>
    </div>
  );
}
